/*
 * BONQUERÉ — Homepage reveal utility  (BNQ-WEB-0449 · Sprint 3.4.14 Gate 3)
 *
 * The ONE piece of new JavaScript in the homepage motion layer. Everything else in Gate 3 is CSS,
 * or behaviour the theme already ships (see the Gate 3 notes in the sprint recon §7): the product
 * carousel, the second-image-on-hover swap and the in-card image swipe are all native Horizon and
 * are deliberately NOT touched here.
 *
 * WHAT IT DOES, AND NOTHING MORE
 *   1. Watches every `[data-bnq-reveal]` root with a single shared IntersectionObserver.
 *   2. When a root enters the viewport it sets `data-bnq-revealed` on it and stops observing it.
 *      The reveal therefore runs ONCE — scrolling back up does not replay it.
 *   3. Numbers the staggered children of each root into `--bnq-i`, which the stylesheet turns into
 *      a transition-delay. Ordering is done here rather than with `:nth-child` because the Horizon
 *      block system nests content at different depths per section, so sibling position is not the
 *      reading order.
 *
 * IT NEVER HIDES ANYTHING. The hold state lives entirely in snippets/bnq-homepage-motion.liquid,
 * is written against server-rendered attributes, and is gated three ways:
 *   · `html.bnq-motion`               — set by the inline head guard, only when motion is wanted;
 *   · `@media (prefers-reduced-motion: no-preference)` — a visitor who asked for less motion can
 *                                       never have content held back, even if this file runs;
 *   · a 2s failsafe in the head guard  — if this file 404s or throws before signalling readiness,
 *                                       `bnq-motion` is dropped and every held element is released.
 * With JavaScript off, `bnq-motion` is never added and the page renders in its final state.
 *
 * THEME EDITOR SAFE. `shopify:section:load` re-scans only the section that was re-rendered, and
 * roots are marked `data-bnq-motion-bound` so repeated re-renders cannot stack observers.
 *
 * Homepage-scoped by construction: the snippet that loads this file is gated on the index template.
 * No dependency, no polling, no scroll listener, no work while the page is idle.
 */
(function () {
  'use strict';

  var html = document.documentElement;

  /* Stand the head guard's failsafe timer down: this file parsed and is running, so the hold state
     now has an owner. Done first, before anything that could throw. */
  html.setAttribute('data-bnq-motion-ready', '');

  /* A visitor who has asked for reduced motion gets no observer at all. Nothing is held back for
     them (the stylesheet's hold block is inside `prefers-reduced-motion: no-preference`), so
     returning here leaves the homepage in its final, fully readable state. */
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* The selector for staggered children is authored ONCE, in the Liquid snippet, and handed over on
     `window.BNQ_MOTION_ITEMS` — the same string is interpolated into the stylesheet's hold rules, so
     the two can never drift. The literal below is only a fallback for the (impossible in practice)
     case of the global being missing. */
  var ITEM_SELECTOR =
    typeof window.BNQ_MOTION_ITEMS === 'string' && window.BNQ_MOTION_ITEMS
      ? window.BNQ_MOTION_ITEMS
      : '.text-block, .link, .button, .bnq-reveal-item';

  function reveal(root) {
    root.setAttribute('data-bnq-revealed', '');
  }

  /* One observer for the whole page. `rootMargin` pulls the trigger 12% up from the bottom edge so a
     section has visibly committed to entering before it starts, rather than animating in the corner
     of the eye; the 0.01 threshold keeps tall sections (the hero, the full-bleed banner) from waiting
     for a fraction of themselves that will never be on screen at once. */
  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        reveal(entries[i].target);
        observer.unobserve(entries[i].target); // reveal-once, and no idle work afterwards
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
  );

  function bind(root) {
    if (root.hasAttribute('data-bnq-motion-bound')) return;
    root.setAttribute('data-bnq-motion-bound', '');

    /* Number the staggered children in document order. A root may narrow the set with
       `data-bnq-reveal-items`; the collection band does this so the stagger counts tiles rather than
       the text blocks inside them. */
    var selector = root.getAttribute('data-bnq-reveal-items') || ITEM_SELECTOR;
    var items;
    try {
      items = root.querySelectorAll(selector);
    } catch (error) {
      items = []; // a malformed override degrades to "no stagger", never to hidden content
    }
    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty('--bnq-i', String(i));
    }

    /* Roots already on screen at bind time (the hero, always) still animate: IntersectionObserver
       delivers even its first observation asynchronously, so the hold state — which came down in the
       server-rendered HTML — has been painted before `data-bnq-revealed` lands. */
    observer.observe(root);
  }

  function scan(scope) {
    var roots = (scope || document).querySelectorAll('[data-bnq-reveal]');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  scan(document);

  /* Theme Editor: re-scan only the subtree that was re-rendered. The re-rendered nodes are new
     elements without `data-bnq-motion-bound`, so they bind once; every other root on the page is
     already bound (or already revealed and unobserved) and is skipped. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
